import { Component, effect, EventEmitter, OnInit, Output } from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { CommonDatePickerComponent } from '@lib-ui/common-date-picker/common-date-picker.component';
import { CommonDropdownComponent } from '@lib-ui/common-dropdawn/common-dropdown.component';
import { FilterPanelService } from './filter-panel.service';
import { Params } from '@angular/router';
import { GroupsSelectorService } from '@lib-services/groups-selector.service';
import { OrderStatusEnum } from '@libs-enums/status.enum';
import { EnumService } from '@lib-services/enum.service';
import { SessionStorageService } from '@lib-services/session-storage.service';
import { debounceTime } from 'rxjs';
import { MatTooltip } from '@angular/material/tooltip';
import { TimeZoneService } from '@lib-services/time-zone.service';
import { GroupsService } from '@lib-services/groups.service';
import { AuthService } from '@lib-ui/auth/auth.service';

@Component({
    selector: 'app-filter-panel',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatDatepickerModule,
        MatInput,
        MatButton,
        MatIconButton,
        MatIcon,
        ReactiveFormsModule,
        MatSelectModule,
        CommonDatePickerComponent,
        CommonDropdownComponent,
        MatMiniFabButton,
        MatTooltip
    ],
    providers: [provideNativeDateAdapter()],
    templateUrl: './filter-panel.component.html',
    styleUrl: './filter-panel.component.scss',
})
export class FilterPanelComponent implements OnInit {
    @Output() setFilters = new EventEmitter<Params>();
    filtersForm: FormGroup;
    moreFilters = false;
    protected readonly StatusEnum = OrderStatusEnum;
    value: { [key: string]: string } = {};

    constructor(
        private fb: FormBuilder,
        protected groupsSelectorService: GroupsSelectorService,
        protected groupsService: GroupsService,
        private filterPanelService: FilterPanelService,
        protected enumService: EnumService,
        protected timeZoneService: TimeZoneService,
        protected authService: AuthService,
        protected sessionStorageService: SessionStorageService,
    ) {
        this.filtersForm = this.fb.group({
            dateFrom: [new Date()],
            group: [''],
            status: [''],
            address: [''],
            phone: [''],
            id: ['', [Validators.min(0)]],
            firstName: [''],
        });

        effect(() => {
            if (this.timeZoneService.currentTimeZone()) {
                this.createFilterParams();
            }
        }, { allowSignalWrites: true });

        effect(() => {
            if (this.authService.companyId()) {
                this.filtersForm.controls['group'].setValue('');
                this.groupsService.currentGroupId.set(null);
            }
        }, { allowSignalWrites: true });
    }

    ngOnInit(): void {
        const storage = this.sessionStorageService.getFiltersValues();
        if (storage) {
            const savedFilters = Object.keys(storage);
            savedFilters.forEach(filter => {
                if(storage[filter] && filter === 'group') {
                    this.filtersForm.controls[filter].setValue(storage[filter]);
                }
            });
        }

        this.filtersForm.valueChanges.pipe(
            debounceTime(800)
        ).subscribe((value) => {
            this.value = value;
            this.groupsService.currentGroupId.set(value['group']);
            this.createFilterParams();
        });

    }

    createFilterParams(): void {
        const propertyMap: { [key: string]: string } = {
            address: 'addressDetails',
            phone: 'contact.phone',
            firstName: 'contact.firstName'
        };

        if (this.value['group']) {
            const updatedValue = { ...this.value };
            updatedValue['dateFrom'] = this.setDayRangeWithOffset(updatedValue['dateFrom']);
            Object.keys(propertyMap).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(updatedValue, key)) {
                    updatedValue[propertyMap[key]] = updatedValue[key];
                    delete updatedValue[key];
                }
            });

            this.sessionStorageService.setFiltersValues(updatedValue);
            this.setFilters.emit(this.filterPanelService.createSearchParams(updatedValue));
        }
    }

    get dateControl(): FormControl {
        return this.filtersForm.get('dateFrom') as FormControl;
    }

    get groupControl(): FormControl {
        return this.filtersForm.get('group') as FormControl;
    }

    showMoreFilters(): void {
        this.moreFilters = !this.moreFilters;
    }

    setDayRangeWithOffset(date: Date | string | number): string {
        const today = new Date(date);
        // Установка начала и конца дня в UTC
        const startOfDay = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 0 - this.timeZoneService.currentTimeZone(), 0, 0));
        const endOfDay = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), (23 - this.timeZoneService.currentTimeZone()), 59, 59));

        return `$btw:${startOfDay.toISOString()},${endOfDay.toISOString()}`;
    }

}
